import Twig from 'twigjs';
import focusWithin from '../components/_focus-within';
import spinner from '../../svg/spinner.svg';
import empty from '../components/_empty';
import cloneDeep from '../components/_cloneDeep';

export default class {

	/**
	 * @param {HTMLElement} node
	 * @param {object}      opts
	 * @param {boolean}     opts.autoload
	 * @param {array}       opts.postType
	 * @param {boolean}     opts.requireTopic
	 * @param {domain}      opts.domain
	 * @param {string}      opts.loadingClass
	 *
	 * @returns {class}
	 */
	constructor( node, opts ) {
		this.node       = node;
		this.form       = this.node.querySelector('.filters__form');
		this.cards      = this.node.querySelector('.filters__content');
		this.pagination = this.node.querySelector('.pagination');
		this.loaded     = false;
		this.choices    = {};

		// default opts
		this.opts = {
			autoload: false,
			postType: [ 'post' ],
			requireTopic: false,
			domain: '',
			loadingClass: 'is-loading',
		};

		// dataset (data-) opts
		for ( let opt in this.opts ) {
			if ( this.opts.hasOwnProperty( opt ) && this.node.dataset[ opt ] ) {
				switch( opt ) {
					// bool
					case 'autoload':
					case 'requireTopic':
						this.opts[ opt ] = this.node.dataset[ opt ] !== false;
					break;
					// comma separated to array
					case 'postType':
						this.opts[ opt ] = this.node.dataset[ opt ].split(',').map( pt => pt.trim() );
					break;
					// string
					default:
						this.opts[ opt ] = this.node.dataset[ opt ].trim();
					break;
				}
			}
		}

		// run time opts
		this.opts = Object.assign( this.opts, opts );

		if ( ! this.form || ! this.cards || ! this.pagination ) {
			// cut the mustard - none of our needed divs exist
			return;
		}

		this.defaultParams = {
			filters: {
				type  : this.opts.postType,
				domain: null,
				topic : null,
			},
			paged: 1,
			sortDirection: 'desc',
			sortField: 'published_at',
			q: '',
		};

		// deep clone default params since object.assign is shallow
		this.params = cloneDeep( this.defaultParams );

		this.queryString  = ''; // query string cache to make sure we don't reload the same data

		// listen for search submit and reset paged
		this.form.addEventListener( 'submit', e => {
			e.preventDefault();
			this.params.paged = 1;
			this.load();
		} );

		// capture pagination link click events
		this.pagination.addEventListener( 'click', e => {
			let target = e.target;

			if ( target ) {
				while( target !== this.pagination ) {
					if ( target.tagName === 'A' && target.dataset.page ) {
						e.preventDefault();
						this.params.paged = parseInt( target.dataset.page, 10 );
						this.load();
						this.scrollToTop();
						return;
					}

					target = target.parentNode;
				}
			}
		} );

		// focusWithin for ie11
		focusWithin( '.filters__filter' );

		// search input node
		this.searchInput = document.querySelector( '.filters__filter--search input' );

		if ( this.searchInput ) {
			this.searchInput.addEventListener( 'change', () => this.params.q = this.searchInput.value );
		}

		this.addChoices();

		// load twig templates
		this.loadTemplates();

		// grab params from the url in case the page was refreshed
		this.getParamsFromURL();

		if ( this.opts.autoload ) {
			// load by default if it's a page refresh so the filters will match results
			this.load( false );
		}

		// handle back/forward
		window.addEventListener( 'popstate', () => {
			this.getParamsFromURL();
			this.load( false );
			this.updateTitle();
		} );
	}

	/**
	 *
	 * @param {object} post
	 * @return {string}
	 */
	renderResult( post ) {
		return Twig.twig( { ref: 'result' } ).render( { post } );
	}

	/**
	 *
	 * @param {object} post
	 * @return {string}
	 */
	renderPagination( pagination ) {
		return Twig.twig( { ref: 'pagination' } ).render( { pagination } );
	}

	/**
	 *
	 * @return {undefined}
	 */
	addChoices() {}

	/**
	 *
	 * @return {undefined}
	 */
	loadTemplates() {}

	/**
	 * listen for select change events
	 *
	 * @return {undefined}
	 */
	listen() {
		for ( let choice in this.choices ) {
			if ( this.choices.hasOwnProperty( choice ) ) {
				this.choices[ choice ].listen();
			}
		}
	}

	/**
	 * stop listening for select change events
	 *
	 * @return {undefined}
	 */
	unlisten() {
		for ( let choice in this.choices ) {
			if ( this.choices.hasOwnProperty( choice ) ) {
				this.choices[ choice ].unlisten();
			}
		}
	}

	/**
	 * @returns {object}
	 */
	getParamsFromURL() {
		this.params = cloneDeep( this.defaultParams );
		const urlParams = new window.URLSearchParams( window.location.search );

		for ( let param in this.defaultParams ) {
			if ( ! this.defaultParams.hasOwnProperty( param ) ) {
				continue;
			}

			let value = urlParams.get( param );

			switch( param ) {
				case 'q':
					this.params.q = value || '';
					this.searchInput.value = this.params.q;
				break;
				case 'paged':
					this.params.paged = Math.max( parseInt( value, 10 ), 1 );
				break;
			}
		}

		return urlParams;
	}

	/**
	 * Process the response from the load call
	 *
	 * @param {object} response
	 * @return {undefined}
	 */
	process( response ) {
		const { posts, facets, pagination } = response.data;

		// process results
		empty( this.cards );

		if ( posts.length ) {
			posts.forEach( post => {
				// external posts in new window
				if ( this.opts.domain && ( new URL( post.url || post.link ) ).host !== this.opts.domain ) {
					post.target = '_blank';
				}

				// render posts results via twig
				this.cards.insertAdjacentHTML( 'beforeend', this.renderResult( post ) );
			} )
		} else {
			this.cards.innerHTML = '<p class="aligncenter">There are no results that match your filters.</p>';
		}

		// process pagination
		empty(this.pagination);

		if ( pagination ) {
			// re-render pagination results via twig
			this.pagination.insertAdjacentHTML( 'beforeend', this.renderPagination( pagination ) );
		}

		if ( facets.length ) {
			facets.forEach( facet => {
				if ( ! this.choices[ facet.name ] ) {
					return;
				}

				facet.filters.sort( ( a, b ) => a.label.localeCompare( b.label ) );

				this.choices[ facet.name ].setOptions( facet.filters );
			} );
		}
	}

	/**
	 * Load the results based on the current state
	 *
	 * @param {bool} history
	 */
	load( history = true ) {
		const queryString = this.getQueryString();
		const xhrUrl = this.getXhrUrl( queryString );

		// let our components know we're loading
		this.node.classList.add(this.opts.loadingClass);

		// empty our nodes
		empty(this.cards);
		empty(this.pagination);

		// show the spinner
		this.cards.innerHTML = `<span class="filters__spinner">${spinner}</span>`;

		// make fetch request
		window.fetch( xhrUrl ).then( response => response.json() ).then( response => {
			this.responseData = response.data;
			this.process( response );

			if ( history ) {
				this.updateTitle();
				window.history.pushState( {}, '', `${window.location.origin}${window.location.pathname}${queryString ? '?' + queryString : ''}` );
			}
		}).catch( error => {
			if ( typeof console === 'object' && typeof console.error === 'function' ) {
				console.error(error);
			}

			this.cards.innerHTML = '<p class="aligncenter">There was an unknown error loading the results.</p>';
		}).finally( () => {
			// let the components know we're done loading
			this.node.classList.remove(this.opts.loadingClass);

			this.scrollToTop();
		});
	}

	/**
	 * Update the title of the document on history api push/pop
	 * @returns {string}
	 */
	updateTitle() {
		let title = document.title;
		let pattern = /Page (\d)+ of (\d+)/;

		// try and get totalPages from the last response
		let totalPages = this.responseData && this.responseData.pagination && this.responseData.pagination.total ? this.responseData.pagination.total : null;

		// if it's not there, grab it from the current title, if it exists
		if ( ! totalPages ) {
			let matches = title.match( pattern );

			if ( matches.length ) {
				totalPages = matches[2];
			}
		}

		let replacement = `Page ${this.params.paged}${totalPages ? ' of ' + totalPages : '' }`;

		// replace page numbers with current page
		if ( this.params.paged > 1 ) {
			if ( ! title.match( pattern ) ) {
				// if no page, replace the first separator with the replacement
				pattern = /\s([-–—:·•*|~«»<>])\s/;
				replacement = ` $1 ${ replacement } $1 `;
			}
		} else {
			replacement = '';
		}

		// do replacement
		title = title.replace( pattern, replacement );

		// replace consecutive separators when going back to page 1 (list is from yoast)
		title = title.replace( /([-–—:·•*|~«»<>])\s+?\1/, '$1');

		document.title = title;

		return title;
	}

	/**
	 * Scroll to the top of the results
	 *
	 * @returns {undefined}
	 */
	scrollToTop() {
		// do this so it doesn't scroll to the content on initial load
		if ( ! this.loaded ) {
			this.loaded = true;
			return;
		}

		// scroll to the top of the results
		let top = Math.max( 0, this.form.getBoundingClientRect().top + ( window.scrollY || window.pageYOffset ) - 10 );

		let offset = [
			document.querySelector('#wpadminbar'),
			document.querySelector('.kl-notices'),
			document.querySelector('.header'),
		].filter( n => n ).reduce( (total, node) => total + node.getBoundingClientRect().height, 0 );

		if ( offset ) {
			top -= offset;
		}

		window.scrollTo(0, top);
	}
}
